"use client";

import { useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import type { AdvancedFilterGroup, FilterContextValue } from "./types";
import type { SimpleTableConfig, BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import { normalizeSerializedFilters } from "@/lib/services/filter-conversion";

/** Serialized view state stored in saved-view `filters` field. */
interface SerializedViewState {
  filters?: Record<string, unknown>;
  advancedFilters?: AdvancedFilterGroup[];
  search?: string;
  [key: string]: unknown;
}

/**
 * Create action creators for filter management.
 *
 * Every action is individually `useCallback`-stable, but the CONTAINER they are
 * returned in must be stable too — it is a dependency of the provider's context
 * value memo, and a fresh object literal here defeated that memo on every
 * render (so every keystroke in table search re-rendered every table row).
 */
export function createFilterActions<T extends BaseEntity>(
  setFilters: React.Dispatch<React.SetStateAction<FilterContextValue["filters"]>>,
  setUiState: React.Dispatch<React.SetStateAction<FilterContextValue["uiState"]>>,
  setSearchTermRaw: (term: string) => void,
  config: SimpleTableConfig<T>,
  router: ReturnType<typeof useRouter>,
  allViews?: Array<{ id: string; name: string; filters?: Record<string, unknown> }>
) {
  const setSimpleFilter = useCallback(
    (field: string, value: unknown) => {
      setFilters((prev) => {
        const newSimple = { ...prev.simple };
        if (value === undefined || value === null || value === "") {
          delete newSimple[field];
        } else {
          newSimple[field] = value;
        }
        return { ...prev, simple: newSimple };
      });
    },
    [setFilters]
  );

  const replaceSimpleFilters = useCallback(
    (filters: Record<string, unknown>) => {
      setFilters((prev) => {
        const next: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(filters)) {
          if (value === undefined || value === null || value === "") continue;
          next[key] = value;
        }
        return { ...prev, simple: next };
      });
    },
    [setFilters]
  );

  const setAdvancedFilters = useCallback(
    (groups: AdvancedFilterGroup[]) => {
      setFilters((prev) => ({
        ...prev,
        advanced: groups,
      }));
    },
    [setFilters]
  );

  const setSearchTerm = useCallback(
    (term: string) => {
      setSearchTermRaw(term);
      setFilters((prev) => ({
        ...prev,
        search: term,
      }));
    },
    [setSearchTermRaw, setFilters]
  );

  const toggleMode = useCallback(() => {
    setUiState((prev) => ({
      ...prev,
      mode: prev.mode === "simple" ? "advanced" : "simple",
    }));
  }, [setUiState]);

  const togglePanel = useCallback(() => {
    setUiState((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  }, [setUiState]);

  const setFilterOperator = useCallback(
    (operator: "AND" | "OR") => {
      setUiState((prev) => ({
        ...prev,
        filterOperator: operator,
      }));
    },
    [setUiState]
  );

  const setShowAdvancedFilters = useCallback(
    (show: boolean) => {
      setUiState((prev) => ({
        ...prev,
        showAdvancedFilters: show,
      }));
    },
    [setUiState]
  );

  const setCustomRangeModal = useCallback(
    (modal: { isOpen: boolean; fieldId: string; fieldLabel: string }) => {
      setUiState((prev) => ({
        ...prev,
        customRangeModal: modal,
      }));
    },
    [setUiState]
  );

  const clearAll = useCallback(() => {
    setFilters({
      simple: {},
      advanced: [],
      search: "",
      activeViewId: null,
    });
    setSearchTermRaw("");
    router.push(window.location.pathname);
  }, [setFilters, setSearchTermRaw, router]);

  const applyPreset = useCallback(
    (viewId: string | null) => {
      // 1. Find saved view in allViews first (includes Supabase views), then fallback to config
      let savedView = viewId ? allViews?.find((v) => v.id === viewId) : null;

      // Fallback to config.savedViews if not found in allViews
      if (!savedView && viewId) {
        savedView = config.savedViews?.find((v) => v.id === viewId) as
          | { id: string; name: string; filters?: Record<string, unknown> }
          | undefined;
      }

      // 2. Clear all current filters (replace, not merge)
      setFilters({
        simple: {},
        advanced: [],
        search: "",
        activeViewId: viewId,
      });
      setSearchTermRaw("");

      // 3. If view exists, apply its filters
      if (savedView?.filters && Object.keys(savedView.filters).length > 0) {
        const rawFilters = savedView.filters as SerializedViewState;

        // Check if this is a SerializedViewState (has nested 'filters' property)
        if (rawFilters.filters && typeof rawFilters.filters === "object") {
          // SerializedViewState format
          const normalized = normalizeSerializedFilters(rawFilters.filters as Record<string, unknown>);
          const searchValue = typeof rawFilters.search === "string" ? rawFilters.search : "";
          setFilters((prev) => ({
            ...prev,
            simple: normalized,
            advanced: Array.isArray(rawFilters.advancedFilters) ? rawFilters.advancedFilters : [],
            search: searchValue,
          }));
          if (searchValue) {
            setSearchTermRaw(searchValue);
          }
        } else {
          // TableFilters format (flat object)
          const normalized = normalizeSerializedFilters(rawFilters as Record<string, unknown>);
          setFilters((prev) => ({
            ...prev,
            simple: normalized,
          }));
        }
      }

      // 4. Update URL to ?view=viewId
      const url = new URL(window.location.href);
      if (viewId) {
        url.searchParams.set("view", viewId);
      } else {
        url.searchParams.delete("view");
      }
      router.push(url.pathname + url.search);
    },
    [config.savedViews, allViews, setFilters, setSearchTermRaw, router]
  );

  return useMemo(
    () => ({
      setSimpleFilter,
      replaceSimpleFilters,
      setAdvancedFilters,
      setSearchTerm,
      setFilterOperator,
      toggleMode,
      togglePanel,
      setShowAdvancedFilters,
      setCustomRangeModal,
      clearAll,
      applyPreset,
    }),
    [
      setSimpleFilter,
      replaceSimpleFilters,
      setAdvancedFilters,
      setSearchTerm,
      setFilterOperator,
      toggleMode,
      togglePanel,
      setShowAdvancedFilters,
      setCustomRangeModal,
      clearAll,
      applyPreset,
    ]
  );
}
